import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  getActiveCompanyId,
  readStoredCompany,
  setActiveCompanyId,
  storeCompany,
} from './active-company-store';
import {
  ActiveCompanyContext,
  SwitchActiveCompanyContext,
  type ActiveCompanyValue,
} from './active-company-context';
import { companySlug, useMe } from './me';

interface Props {
  children: ReactNode;
}

/**
 * Resuelve la empresa activa y la publica: al contexto (para el chrome) y al store
 * del cliente API (para el header X-Active-Company). Por defecto es la primaria del
 * usuario; si eligió otra de SUS empresas, esa (recordada en el navegador y validada
 * contra /me en cada carga). Debe vivir dentro de ApiProvider y del QueryClient.
 */
export function ActiveCompanyProvider({ children }: Props): ReactElement {
  const me = useMe();
  const qc = useQueryClient();
  const data = me.data?.data;
  const subject = data?.auth_subject ?? null;
  const companyIds = useMemo(() => data?.company_ids ?? [], [data]);

  // Elección hecha en esta sesión; si no hay, la recordada en el navegador.
  const [choice, setChoice] = useState<{ subject: string; id: string } | null>(null);
  const wanted =
    subject === null ? null : choice?.subject === subject ? choice.id : readStoredCompany(subject);
  const valid = wanted !== null && companyIds.includes(wanted);
  const activeCompanyId = valid ? wanted : (data?.active_company_id ?? null);

  // Sync al store DURANTE el render (no en useEffect): los effects de los hijos
  // corren antes que los del padre, así que un useEffect aquí dejaría a una query
  // hija dispararse sin el header X-Active-Company (fuga/uso de la empresa
  // equivocada en carga fría/deep-link). El set en render lo evita: el store
  // queda listo antes de que los hijos monten sus queries. Guardado e idempotente.
  if (getActiveCompanyId() !== activeCompanyId) {
    setActiveCompanyId(activeCompanyId);
  }

  // Una elección recordada que ya no es del usuario (el LMS le quitó la empresa) se olvida.
  useEffect(() => {
    if (subject !== null && wanted !== null && !valid && data !== undefined) {
      storeCompany(subject, null);
    }
  }, [subject, wanted, valid, data]);

  const switchCompany = useCallback(
    (companyId: string) => {
      if (subject === null || !companyIds.includes(companyId) || companyId === activeCompanyId) {
        return;
      }
      storeCompany(subject, companyId);
      setChoice({ subject, id: companyId });
      // Antes de refetchear: las peticiones nuevas ya salen con la empresa elegida.
      setActiveCompanyId(companyId);
      // Ningún dato de la empresa anterior queda visible: se descarta y se pide de nuevo.
      // /me no depende de la empresa activa y se conserva.
      void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
    [subject, companyIds, activeCompanyId, qc],
  );

  const value = useMemo<ActiveCompanyValue>(() => {
    const companies = data?.companies ?? [];
    const active = companies.find((c) => c.id === activeCompanyId) ?? null;
    return {
      activeCompanyId,
      activeCompanyName: active?.name ?? null,
      slug: active ? companySlug(active.name) : null,
      companies,
      crossCompany: data?.cross_company ?? false,
      isLoading: me.isLoading,
      isError: me.isError,
    };
  }, [data, activeCompanyId, me.isLoading, me.isError]);

  return (
    <ActiveCompanyContext.Provider value={value}>
      <SwitchActiveCompanyContext.Provider value={switchCompany}>
        {children}
      </SwitchActiveCompanyContext.Provider>
    </ActiveCompanyContext.Provider>
  );
}
